import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { List, X } from "@phosphor-icons/react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_pixora-moments/artifacts/xaiv43xy_RECTO.png";

const navLinks = [
  { name: "Accueil", path: "/" },
  { name: "Tarifs Particuliers", path: "/tarifs-particuliers" },
  { name: "Tarifs Pro", path: "/tarifs-professionnels" },
  { name: "Contact", path: "/contact" },
];

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const lastYRef = useRef(0);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      const lastY = lastYRef.current;
      const delta = y - lastY;

      // Ignore tiny scrolls to avoid jitter
      if (Math.abs(delta) < 6) return;

      // Always show header near the very top
      if (y < 80) {
        setHidden(false);
      } else if (delta > 0) {
        // Scrolling down → hide (and close mobile menu if open)
        setHidden(true);
        setMobileMenuOpen(false);
      } else {
        // Scrolling up → show
        setHidden(false);
      }
      lastYRef.current = y;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Never hide the header while the mobile menu is expanded
  const shouldHide = hidden && !mobileMenuOpen;

  return (
    <motion.header
      initial={false}
      animate={{ y: shouldHide ? "-100%" : "0%" }}
      transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
      className="fixed top-0 left-0 right-0 z-50 glass"
      data-testid="header"
      data-hidden={shouldHide ? "true" : "false"}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="flex items-center justify-between h-20 sm:h-24 md:h-32 lg:h-36">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 py-1" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="PIXORA — Photobooth Premium" 
              className="h-14 sm:h-16 md:h-24 lg:h-28 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-base font-medium tracking-wide transition-colors hover:text-[#C9A962] ${
                  location.pathname === link.path 
                    ? "text-[#C9A962]" 
                    : "text-[#B8A88A]"
                }`}
                data-testid={`nav-link-${link.path.replace("/", "") || "home"}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:block">
            <Link to="/reservation" data-testid="reserve-btn-header">
              <button className="btn-primary text-base px-7 py-3">
                Réserver
              </button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X size={24} /> : <List size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden overflow-hidden"
              data-testid="mobile-menu"
            >
              <div className="py-4 space-y-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block text-base font-medium transition-colors hover:text-[#C9A962] ${
                      location.pathname === link.path 
                        ? "text-[#C9A962]" 
                        : "text-[#B8A88A]"
                    }`}
                    data-testid={`mobile-nav-link-${link.path.replace("/", "") || "home"}`}
                  >
                    {link.name}
                  </Link>
                ))}
                <Link 
                  to="/reservation" 
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-reserve-btn"
                >
                  <button className="btn-primary w-full text-sm mt-4">
                    Réserver
                  </button>
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </motion.header>
  );
}
